import VtMerkki from "./VtMerkki";
import LinjatarkastusRaportti from "./LinjatarkastusRaportti";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Allekirjoitusruutu, { type AllekirjoitusruutuKahva } from "./Allekirjoitusruutu";
import { naytaPvm } from "./pvm";
import { api, ApiError, punchSuljettu, punchinLuokka, type Hitsi, type Linjatarkastus, type Luovutustila, type Punch } from "./api";
import { luokanNimi } from "./Punchit";
import { naytaHitsinumero } from "./HitsiTunnus";
import { useKirjaustunniste } from "./kirjaustunniste";
import Liitteet, { KameraKuvake, lueBase64 } from "./Liitteet";
import { LinjanPunchPaneeli, punchinTekstiNimetta } from "./LinjanPunchPaneeli";
import { TILAVARI } from "./Tila";
import { t as tr, virheTeksti, type KaannosAvain } from "./kieli";
import { Luokkavalitsin, luokkavalintaKunnossa } from "./Luokkavalitsin";

/* Linjatarkastus (walkdown) — mekaanisen valmiuden tarkastus ennen painekoetta.
   9 kohtaa JS Groupin Linjatarkastussuunnitelman luvusta 7 (7.1–7.9), kukin
   OK / Punch / N/A. Hyväksytty walkdown = osa "linja VALMIS" -porttia. */

export const KOHDAT: [string, KaannosAvain][] = [
  ["7.1", "ltar.k71"], ["7.2", "ltar.k72"], ["7.3", "ltar.k73"], ["7.4", "ltar.k74"], ["7.5", "ltar.k75"],
  ["7.6", "ltar.k76"], ["7.7", "ltar.k77"], ["7.8", "ltar.k78"], ["7.9", "ltar.k79"],
];
const TULOS = ["", "ok", "punch", "na"];
// tyhjä on sana, ei viiva (Tila.tsx)
const tulosNimi = (v: string): string => v === "" ? tr("ltar.ei_tarkastettu") : v === "ok" ? "OK" : v === "punch" ? "Punch" : v === "na" ? "N/A" : v;
// punch on vika eikä varoitus: se estää hyväksynnän
// (voiHyvaksya = taytetty && punchit === 0), joten linjatarkastus ei
// mene läpi ennen kuin kohta on korjattu. Vikaväri tokenista.
const tulosVari: Record<string, string> = { ok: TILAVARI.kunnossa, punch: TILAVARI.vika, na: TILAVARI.ei_mitattu, "": TILAVARI.ei_mitattu };

/** vt_peruste (T642): kohdan 7.2 OK:n peruste, kun linjalla on hitsattuja hitsejä ilman hyväksyttyä VT:tä. */
type Kohdat = Record<string, { tulos: string; huomio?: string; vt_peruste?: string }>;

// Suomen päivä, ei UTC:n (klo 00–03 UTC-päivä on vielä eilinen) — sama kuin NdtKirjaus/VtKirjaus.
const tanaan = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Helsinki" });
// 249: hitsi tunnistetaan numerolla tai tarralla (JSG-26-2217 tai 2217); kirjainkoko ja välilyönnit eivät erota.
// Monitulkintainen syöte (sama numero kahdella hitsillä) ei valitse kumpaakaan.
type HitsiTunnus = Pick<Hitsi, "id" | "hitsinumero" | "tarra">;
const norm = (x: string | null | undefined) => (x ?? "").replace(/\s+/g, "").toUpperCase();
export function etsiHitsi(hitsit: HitsiTunnus[], syote: string): HitsiTunnus | null {
  const s = norm(syote);
  if (!s) return null;
  const osuu = hitsit.filter((h) => norm(h.hitsinumero) === s || (!!h.tarra && (norm(h.tarra) === s || norm(h.tarra).replace(/^[A-Z]+-\d+-/, "") === s)));
  return osuu.length === 1 ? osuu[0]! : null;
}

/** onTallennettu (päätös 90): nippu siirtyy seuraavaan linjaan onnistuneen tallennuksen jälkeen. */
/** oletusTarkastaja (Toni 17.10.2026 «Oletus lie kirjautunut?» · «ja pvm oletus tänään»): kirjautuneen nimi ja tämä päivä tyhjiin
 *  kenttiin keskeneräisellä tarkastuksella. Muokattavissa: jälkikäteen kirjattava tarkastus kirjataan oikealla tarkastajalla ja päivällä. */
export default function LinjaTarkastus({ linjaId, saaMuokata: saaRoolista, sharepointissa, onTallennettu, oletusTarkastaja, kuvatErikseen = false, versio = 0 }: { linjaId: number; saaMuokata: boolean; sharepointissa?: boolean; onTallennettu?: (hyvaksytty: boolean) => void; oletusTarkastaja?: string;
  /** Toni 19.10.2026 klo 11.50: tarkastuskuvat-osio näytetään isäntänäkymässä isometrin yläpuolella (Selailu), ei tässä. */
  kuvatErikseen?: boolean;
  /** Kasvaa kun isäntänäkymän kuvaosio muuttui (paperipöytäkirjan laji vaikuttaa hyväksyntään). */
  versio?: number }) {
  // Toni 12.10.2026: instanssissa jossa linjatarkastus kirjataan SharePointiin (LINJATARKASTUS_ISANTA=sharepoint) lomake on vain luku.
  const saaMuokata = saaRoolista && !sharepointissa;
  const [t, setT] = useState<Linjatarkastus | null>(null);
  const [ladattu, setLadattu] = useState(false);
  const [kohdat, setKohdat] = useState<Kohdat>({});
  const [tarkastaja, setTarkastaja] = useState("");
  const [pvm, setPvm] = useState("");
  const [huomiot, setHuomiot] = useState("");
  const [tallentaa, setTallentaa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  // 177 (Toni 26.9.2026): hyväksyntä = luovutus asiakkaalle. Edustaja on aina paikalla linjatarkastuksessa.
  const [lt, setLt] = useState<Luovutustila | null>(null);
  const [edustaja, setEdustaja] = useState("");
  // Oletus tänään (kävely 26.9.: tyhjä oletus esti hyväksynnän vaikka edustaja oli annettu). Muokattavissa.
  // Suomen päivä, ei UTC:n: klo 00–03 UTC-päivä on vielä eilinen. 179 (Toni 27.9.): mennyt käy, tuleva ei.
  const [aPeruste, setAPeruste] = useState("");
  // 249 (Toni 17.10.2026): kohdan merkinnät ovat linjan puncheja (sama lista, sama A/B/C), kohta ja hitsi kiinni.
  const [punchit, setPunchit] = useState<Punch[] | null>(null);
  const [hitsit, setHitsit] = useState<Hitsi[] | null>(null);
  const [uusi, setUusi] = useState<{ kohta: string; vika: string; luokka: "" | "A" | "B" | "C"; hitsi: string; missa: string; vikaluokka: string } | null>(null);
  const [uusiVirhe, setUusiVirhe] = useState<string | null>(null);
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();
  // Toni 17.10.2026 klo 07.35/07.40: ilmoitus avaa linjan avoimet punchit saman sivun päälle; merkintärivin kuvat avattavissa.
  const [paneeliAuki, setPaneeliAuki] = useState(false);
  const [kuvatAuki, setKuvatAuki] = useState<number | null>(null);
  const [infoAuki, setInfoAuki] = useState<string | null>(null);   // T689: kohdan «Mitä tarkistetaan?» auki
  // 251 (T621): asiakkaan edustajan allekirjoitus piirretään ruutuun; tallennetaan hyväksynnän yhteydessä.
  const ruutu = useRef<AllekirjoitusruutuKahva>(null);
  const [piirretty, setPiirretty] = useState(false);
  // T635 (Toni 20.10.2026 klo 07.06): hyväksytystä tarkastuksesta tulostettava linjatarkastusraportti.
  // Keskeneräisenä (Toni 22.10.2026 klo 11.45): kesken olevasta tarkastuksesta sama raportti merkinnällä, ilman allekirjoitusta.
  const [raportti, setRaportti] = useState<null | "hyvaksytty" | "kesken">(null);

  const lataa = useCallback(async () => {
    setLadattu(false);
    try {
      const r = await api.linjatarkastus(linjaId);
      setT(r);
      kohdatRef.current = r?.kohdat ?? {};
      palvelimellaRef.current = r?.kohdat ?? {};
      setKohdat(r?.kohdat ?? {});
      // Oletukset vain keskeneräiselle ja kirjoittajalle: hyväksytyn (myös tuodun) tyhjään kenttään ei keksitä tarkastajaa.
      const oletus = saaMuokata && r?.tila !== "hyvaksytty";
      setTarkastaja(r?.tarkastaja || (oletus ? oletusTarkastaja ?? "" : ""));
      setPvm(r?.pvm || (oletus ? tanaan() : ""));
      setHuomiot(r?.huomiot ?? "");
    } catch { setT(null); }
    // Luovutustila erikseen: sen puuttuminen ei saa näyttää nollalta A-punchilta — silloin hyväksyntä on estetty.
    try { setLt(await api.luovutustila(linjaId)); } catch { setLt(null); }
    try { setPunchit(await api.punchit(linjaId)); } catch { setPunchit(null); }
    finally { setLadattu(true); }
  }, [linjaId, saaMuokata, oletusTarkastaja]);
  // Isäntänäkymän kuvaosio muuttui (esim. allekirjoitettu paperipöytäkirja liitettiin): luovutustila uudelleen, lomake ennallaan.
  useEffect(() => { if (versio > 0) void api.luovutustila(linjaId).then(setLt).catch(() => {}); }, [versio, linjaId]);

  async function avaaUusi(kohta: string) {
    setUusiVirhe(null);
    setUusi({ kohta, vika: "", luokka: "", hitsi: "", missa: "", vikaluokka: "" });
    if (hitsit === null) { try { setHitsit(await api.hitsit(linjaId, 500)); } catch { setHitsit([]); } }
  }
  async function lisaaMerkinta() {
    if (!uusi) return;
    if (!uusi.vika.trim()) { setUusiVirhe(tr("pc.kuvaa_vika")); return; }
    if (!uusi.luokka) { setUusiVirhe(tr("pc.valitse_luokka_virhe")); return; }
    if (!luokkavalintaKunnossa({ missa: uusi.missa, vika: uusi.vikaluokka, lisatieto: uusi.vika })) { setUusiVirhe(tr("virhe.luokat_puuttuvat")); return; }
    const h = uusi.hitsi.trim() ? etsiHitsi(hitsit ?? [], uusi.hitsi) : null;
    if (uusi.hitsi.trim() && !h) { setUusiVirhe(tr("ltar.hitsi_ei_loydy", { h: uusi.hitsi.trim() })); return; }
    try {
      await api.lisaaPunch(linjaId, { kirjaustunniste: tunniste, vika: uusi.vika.trim(), luokka: uusi.luokka,
        linjatarkastus_kohta: uusi.kohta, hitsi_id: h?.id ?? null, luokka_missa: uusi.missa, luokka_vika: uusi.vikaluokka,
        ...(tarkastaja.trim() && { huomioinut: tarkastaja.trim() }), ...(pvm && { huomio_pvm: pvm }) });
      vaihdaTunniste();
      // Merkintä on punch: kohdan tulos punchiksi (tallentuu lomakkeen tallennuksessa kuten ennenkin).
      aseta(uusi.kohta, "tulos", "punch");
      setUusi(null);
      setPunchit(await api.punchit(linjaId));
    } catch (e) { setUusiVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe")); }
  }
  useEffect(() => { void lataa(); }, [lataa]);

  /* Kohta tallentuu heti merkittäessä (Toni 20.10.2026 klo 12.40): ennen tätä kohdat 7.1–7.9 olivat vain selaimen tilaa, kunnes
     painettiin Tallenna, ja pekilossa 20.10. merkinnät katosivat sivun latauksessa (palvelimelle 0 tallennuspyyntöä). Tulos tallentuu
     valinnasta, huomio sekunnin tauon jälkeen tai kentästä poistuttaessa. Tallennus lähettää lomakkeen nykytilan (sama kuin Tallenna),
     joten ensimmäinen merkintä luo tarkastuksen. Tallennukset jonossa yksi kerrallaan, ja jokainen lähettää uusimman tilan. Epäonnistunut
     merkintä jää näkyviin tilalla «Tallennus epäonnistui» ja painikkeella «Yritä uudelleen» — se ei katoa. */
  const kohdatRef = useRef<Kohdat>({});
  /** Viimeksi palvelimelle mennyt tila: kohdan 7.2 OK, joka odottaa VT-perustetta, lähetetään sen arvolla (T642). */
  const palvelimellaRef = useRef<Kohdat>({});
  const lomakeRef = useRef({ pvm: "", tarkastaja: "", huomiot: "" });
  lomakeRef.current = { pvm, tarkastaja, huomiot };
  /* T642 kohta 2 (Toni 20.10.2026 klo 16.50): kohtaa 7.2 ei voi merkitä OK:ksi, jos linjalla on hitsattuja hitsejä ilman hyväksyttyä VT:tä,
     ellei tarkastaja kirjoita perustetta. Palvelin tarkistaa saman (409 vt_peruste_puuttuu). OK ilman perustetta jää odottamaan («peruste»)
     eikä lähde palvelimelle; muut kohdat tallentuvat silti, ja niiden mukana 7.2 lähtee viimeksi tallennetulla arvollaan. */
  const vtPuuttuu = lt?.vt_puuttuu ?? 0;
  const vtRef = useRef(0);
  vtRef.current = vtPuuttuu;
  const vtOdottaa = (k: Kohdat) => k["7.2"]?.tulos === "ok" && vtRef.current > 0 && !(k["7.2"]?.vt_peruste ?? "").trim()
    && palvelimellaRef.current["7.2"]?.tulos !== "ok";
  const [kohtaTila, setKohtaTila] = useState<Record<string, "tallentaa" | "tallennettu" | "virhe" | "peruste">>({});
  const tallennusjono = useRef<Promise<void>>(Promise.resolve());
  const odottavat = useRef(new Set<string>());
  const huomioAjastin = useRef<number | null>(null);
  function tallennaKohdat(muuttuneet: string[]) {
    if (!muuttuneet.length) return;
    setKohtaTila((s) => ({ ...s, ...Object.fromEntries(muuttuneet.map((n) => [n, "tallentaa" as const])) }));
    tallennusjono.current = tallennusjono.current.then(async () => {
      const l = lomakeRef.current;
      const odottaa = vtOdottaa(kohdatRef.current);
      const kohdat = odottaa
        ? { ...kohdatRef.current, "7.2": { ...kohdatRef.current["7.2"], tulos: palvelimellaRef.current["7.2"]?.tulos ?? "" } }
        : kohdatRef.current;
      try {
        await api.tallennaLinjatarkastus(linjaId, { pvm: l.pvm || null, tarkastaja: l.tarkastaja.trim() || null,
          huomiot: l.huomiot.trim() || null, kohdat });
        palvelimellaRef.current = kohdat;
        setKohtaTila((s) => ({ ...s, ...Object.fromEntries(muuttuneet.map((n) => [n, n === "7.2" && odottaa ? "peruste" as const : "tallennettu" as const])) }));
      } catch {
        setKohtaTila((s) => ({ ...s, ...Object.fromEntries(muuttuneet.map((n) => [n, "virhe" as const])) }));
      }
    });
  }
  function huuhteleHuomiot() {
    if (huomioAjastin.current !== null) { window.clearTimeout(huomioAjastin.current); huomioAjastin.current = null; }
    const n = [...odottavat.current]; odottavat.current.clear(); tallennaKohdat(n);
  }
  useEffect(() => () => { if (huomioAjastin.current !== null) window.clearTimeout(huomioAjastin.current); }, []);
  const aseta = (tunnus: string, kentta: "tulos" | "huomio" | "vt_peruste", arvo: string) => {
    const k = kohdatRef.current;
    const uusi = { ...k, [tunnus]: { ...k[tunnus], tulos: k[tunnus]?.tulos ?? "", [kentta]: arvo } };
    kohdatRef.current = uusi; setKohdat(uusi);
    if (kentta === "tulos" && vtOdottaa(uusi) && tunnus === "7.2") { setKohtaTila((s) => ({ ...s, "7.2": "peruste" })); return; }
    if (kentta === "tulos") { tallennaKohdat([tunnus]); return; }
    odottavat.current.add(tunnus);
    if (huomioAjastin.current !== null) window.clearTimeout(huomioAjastin.current);
    huomioAjastin.current = window.setTimeout(huuhteleHuomiot, 1000);
  };

  const punchKohtia = KOHDAT.filter(([n]) => kohdat[n]?.tulos === "punch").length;
  const taytetty = KOHDAT.every(([n]) => kohdat[n]?.tulos);
  const avoimiaA = lt?.avoimia_a ?? null;
  const aKunnossa = avoimiaA === 0 || (avoimiaA !== null && lt!.saa_poiketa && aPeruste.trim() !== "");
  // 222 (T466, Toni 10.10.2026): hyväksyntä ei ole luovutus. Edustaja vaaditaan kohteen asetuksen mukaan (oletus kyllä).
  const edustajaPakollinen = lt?.edustaja_pakollinen !== false;
  const luovutusKunnossa = !edustajaPakollinen || edustaja.trim() !== "";
  // 251 (T621, Toni 19.10.2026 klo 11.50): edustajan allekirjoitus piirretty (tai jo tallennettu) tai allekirjoitettu paperipöytäkirja liitteenä.
  const allekirjoitusKunnossa = !edustajaPakollinen || piirretty || !!lt?.allekirjoitus || (lt?.paperipoytakirjoja ?? 0) > 0;
  const vtKunnossa = !(kohdat["7.2"]?.tulos === "ok" && vtPuuttuu > 0 && !(kohdat["7.2"]?.vt_peruste ?? "").trim());
  const voiHyvaksya = taytetty && punchKohtia === 0 && vtKunnossa && luovutusKunnossa && allekirjoitusKunnossa && aKunnossa;
  const syy = !taytetty ? tr("ltar.syy_tayta")
    : punchKohtia > 0 ? tr("ltar.syy_punchit", { n: punchKohtia })
    : !vtKunnossa ? tr("ltar.syy_vt", { n: vtPuuttuu })
    : !luovutusKunnossa ? tr("ltar.syy_edustaja")
    : !allekirjoitusKunnossa ? tr("ltar.syy_allekirjoitus")
    : avoimiaA === null ? tr("ltar.syy_ei_tilaa")
    : !aKunnossa ? tr("ltar.syy_a", { n: avoimiaA }) : "";

  async function tallenna(hyvaksy: boolean) {
    setTallentaa(true); setVirhe(null);
    try {
      // 251: piirretty allekirjoitus tallennetaan ennen hyväksyntää (palvelin vaatii sen hyväksynnässä).
      const kuva = hyvaksy ? ruutu.current?.kuva() : null;
      if (kuva) await api.tallennaAllekirjoitus(linjaId, { nimi: edustaja.trim(), kuva });
      await api.tallennaLinjatarkastus(linjaId, {
        pvm: pvm || null, tarkastaja: tarkastaja.trim() || null, huomiot: huomiot.trim() || null,
        kohdat, ...(hyvaksy && { tila: "hyvaksytty",
          asiakkaan_edustaja: edustaja.trim() || null,
          ...(avoimiaA && avoimiaA > 0 && { a_poikkeama_peruste: aPeruste.trim() }) }),
      });
      await lataa();
      onTallennettu?.(hyvaksy);
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  if (!ladattu) return <div className="dim">{tr("ltar.ladataan")}</div>;

  const lukittu = t?.tila === "hyvaksytty";
  const muokattava = saaMuokata && !lukittu;
  // T332 (Toni 14.9.2026): tunnistetieto (tarkastaja, pvm, huomiot) korjataan hyväksyntää menettämättä; tulokset lukittuvat.
  const tunnistetietoMuokattava = saaMuokata;

  return (
    <div>
      {raportti && <LinjatarkastusRaportti linjaId={linjaId} keskenerainen={raportti === "kesken"} sulje={() => setRaportti(null)} />}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }} data-osio-otsikko="">{tr("ltar.otsikko")}</h3>
        {t?.tila === "hyvaksytty" && (
          /* Napin väri (Toni 21.10.2026 klo 06.55): sama pääluokka kuin «Hyväksy linjatarkastus», ei uutta väriä. */
          <button type="button" className="nappi nappi--paa" data-ltr-avaa="" onClick={() => setRaportti("hyvaksytty")}>{tr("ltr.tulosta_nappi")}</button>
        )}
        {t && t.tila !== "hyvaksytty" && (
          /* Napin muoto (Toni 22.10.2026 klo 12.35 ja korjaus 12.40): ytimen varoitusnappi — katkoviiva, vinoraidoitus ja ⚠ tyyliluokasta,
             väri instanssin --varoitus — jotta keskeneräisyys huomataan ennen tulostusta myös, kun varoitusväri on lähellä brändiväriä. */
          <button type="button" className="nappi nappi--varoitus" data-ltr-avaa-kesken="" onClick={() => setRaportti("kesken")}>{tr("ltr.tulosta_keskenerainen_nappi")}</button>
        )}
        {/* 203: puuttuva VT tehdään linjatarkastuksen yhteydessä — merkki näkyy tarkastajalle tässä, ei estä hyväksyntää. */}
        <div style={{ flexBasis: "100%", order: 3 }}><VtMerkki n={lt?.vt_puuttuu} ilmanTulosta={lt?.vt_ilman_tulosta} /></div>
        {t?.tila === "hyvaksytty"
          /* Tuotu hyväksyntä (SharePoint-indeksi, 109) on ilman Sampon
             hyväksyjää: hyväksyjä on lähteen allekirjoitettu pöytäkirja.
             Se sanotaan — viiva tai tyhjä lukisi puutteena, eikä rivin
             ympärille keksitä ihmistä jotta sarake näyttäisi täydeltä. */
          ? <span style={{ color: TILAVARI.kunnossa, fontWeight: 700, fontSize: 13 }}>✓ {tr("ltar.hyvaksytty")}{t.hyvaksyja ? ` · ${t.hyvaksyja}` : t.isantajarjestelma === "sharepoint" ? ` · ${tr("ltar.lahteen_poytakirja")}` : ""}{t.pvm ? ` · ${naytaPvm(t.pvm)}` : ""}</span>
          : <span className="dim" style={{ fontSize: 13 }}>{t ? tr("ltar.kesken") : tr("ltar.ei_aloitettu")}{punchKohtia > 0 ? ` · ${punchKohtia} punch` : ""}</span>}
      </div>

      {/* Päätös 90 (Toni 6.10.2026): Sampo on isäntä tässä tarkastuksessa; SharePointin poikkeava tieto näytetään eikä korvaa. */}
      {(t?.ristiriidat ?? []).map((r) => (
        <p key={r.id} className="huom" data-tarkastus-ristiriita={r.id} style={{ margin: "6px 0" }}>{r.kuvaus}</p>
      ))}
      <div className="scroll">{/* 6.10.2026: taulukko vierii omassa säiliössään puhelimessa (linjanäkymä 375 px) */}
      <table className="tbl" style={{ fontSize: 12.5, marginTop: 8 }}>
        <tbody>
          {KOHDAT.map(([tunnus, otsikko]) => {
            const kh = kohdat[tunnus] ?? { tulos: "", huomio: "" };
            const merkinnat = (punchit ?? []).filter((p) => p.linjatarkastus_kohta === tunnus);
            return (
              <Fragment key={tunnus}>
              <tr>
                <td className="mono" style={{ width: 34, verticalAlign: "top", color: "var(--himmea)" }}>{tunnus}</td>
                <td style={{ verticalAlign: "top" }}>{tr(otsikko)}{" "}
                  {/* T689 (Toni 30.10.2026 klo 14.15): «Mitä tarkistetaan?» — avautuu kohdan alle ja sulkeutuu. */}
                  <button type="button" className="tekstilinkki" data-ltar-info={tunnus} aria-expanded={infoAuki === tunnus}
                    title={tr("ltar.info_nappi")} aria-label={`${tr("ltar.info_nappi")} ${tunnus}`} style={{ minWidth: 32, minHeight: 32 }}
                    onClick={() => setInfoAuki(infoAuki === tunnus ? null : tunnus)}>ⓘ</button></td>
                <td style={{ width: 90, verticalAlign: "top" }}>
                  {muokattava
                    ? <select value={kh.tulos} data-ltar-kohta={tunnus} onChange={(e) => aseta(tunnus, "tulos", e.target.value)}>
                        {TULOS.map((v) => <option key={v} value={v}>{tulosNimi(v)}</option>)}
                      </select>
                    : <span style={{ fontWeight: 700, color: tulosVari[kh.tulos] }}>{tulosNimi(kh.tulos)}</span>}
                  {kohtaTila[tunnus] && (
                    <div data-ltar-kohta-tila={kohtaTila[tunnus]} role={kohtaTila[tunnus] === "virhe" ? "alert" : "status"}
                         style={{ fontSize: 12, marginTop: 2, color: kohtaTila[tunnus] === "virhe" ? "var(--puna-tumma)" : "var(--himmea)" }}>
                      {kohtaTila[tunnus] === "tallentaa" ? tr("ltar.kohta_tallentaa")
                        : kohtaTila[tunnus] === "tallennettu" ? tr("ltar.kohta_tallennettu")
                        : kohtaTila[tunnus] === "peruste" ? tr("ltar.vt_peruste_odottaa")
                        : <>{tr("ltar.kohta_virhe")}{" "}
                            <button type="button" className="nappi nappi--kevyt" style={{ fontSize: 12 }} data-ltar-kohta-yrita={tunnus}
                              onClick={() => tallennaKohdat([tunnus])}>{tr("ltar.kohta_yrita")}</button></>}
                    </div>
                  )}
                </td>
                <td style={{ verticalAlign: "top" }}>
                  {muokattava
                    ? <input value={kh.huomio ?? ""} placeholder={tr("ltar.huomio")} style={{ width: "100%", minWidth: 120 }}
                        data-ltar-kohta-huomio={tunnus}
                        onChange={(e) => aseta(tunnus, "huomio", e.target.value)} onBlur={huuhteleHuomiot} />
                    : <span className="dim">{kh.huomio ?? ""}</span>}
                  {tunnus === "7.2" && kh.tulos === "ok" && (vtPuuttuu > 0 || kh.vt_peruste) && (muokattava
                    ? <label style={{ display: "block", marginTop: 4 }} data-ltar-vt-peruste-otsikko="">{tr("ltar.vt_peruste", { n: vtPuuttuu })}
                        <input value={kh.vt_peruste ?? ""} style={{ width: "100%", minWidth: 120 }} data-ltar-vt-peruste=""
                          onChange={(e) => aseta(tunnus, "vt_peruste", e.target.value)} onBlur={huuhteleHuomiot} /></label>
                    : <span className="dim" data-ltar-vt-peruste-teksti="" style={{ display: "block" }}>{tr("ltar.vt_peruste", { n: vtPuuttuu })}: {kh.vt_peruste ?? ""}</span>)}
                </td>
              </tr>
              {infoAuki === tunnus && (
                <tr data-ltar-info-teksti={tunnus}>
                  <td />
                  <td colSpan={3} style={{ paddingTop: 0, fontSize: 13 }}>
                    <div><b>{tr("ltar.info_tarkista")}:</b> {tr(`ltar.k${tunnus.replace(".", "")}_tarkista` as KaannosAvain)}</div>
                    <div><b>{tr("ltar.info_esim")}:</b> {tr(`ltar.k${tunnus.replace(".", "")}_esim` as KaannosAvain)}</div>
                    <div className="dim">{tr("ltar.info_alarivi")}</div>
                  </td>
                </tr>
              )}
              {/* 249: merkinnät (punchit) kohdan alla — useita, kukin koko kohtaan tai yhteen hitsiin. Muokkaus ei vaadi
                  keskeneräistä tarkastusta: punch voidaan kirjata myös hyväksytyn linjan kohtaan, ja A-luokka estää kuten ennen. */}
              {(merkinnat.length > 0 || saaMuokata) && (
                <tr data-ltar-merkinnat={tunnus}>
                  <td />
                  <td colSpan={3} style={{ paddingTop: 0 }}>
                    {merkinnat.map((p) => {
                      const lk = punchinLuokka(p.luokka);
                      return (
                        <div key={p.id} data-ltar-merkinta={p.id} style={{ fontSize: 12.5, margin: "2px 0" }}>
                          {lk && <span className={lk.tunnus === "A" ? "merkki merkki--vika" : "merkki"} style={{ marginRight: 6 }}>{lk.tunnus}</span>}
                          <span style={punchSuljettu(p) ? { textDecoration: "line-through" } : undefined}>{punchinTekstiNimetta(p)}</span>
                          <span className="dim"> · {p.hitsi_id
                            ? `${tr("pc.hitsi_pieni")} ${naytaHitsinumero(p.hitsinumero) ?? ""}${p.tarra ? ` (${p.tarra})` : ""}`
                            : tr("ltar.merkinta_kohta")}
                            {punchSuljettu(p) ? ` · ${tr("ltar.merkinta_korjattu")}` : ""}</span>
                          {" "}<button type="button" className="nappi nappi--kevyt" style={{ fontSize: 12 }} data-ltar-merkinta-kuvat={p.id}
                            onClick={() => setKuvatAuki(kuvatAuki === p.id ? null : p.id)}>
                            {kuvatAuki === p.id ? tr("pc.sulje_kuvat") : (p.liitteita ? tr("hl.kuvat_n", { n: p.liitteita }) : (saaMuokata ? tr("hl.lisaa_kuva") : tr("pc.ei_kuvia")))}</button>
                          {kuvatAuki === p.id && (
                            <Liitteet pienoiskuvat kohdeTaulu="punch" kohdeId={p.id} saaMuokata={saaMuokata} saaPoistaa={false}
                                      otsikko={tr("pu.kuvat")} muuttui={() => void api.punchit(linjaId).then(setPunchit)} />
                          )}
                        </div>
                      );
                    })}
                    {saaMuokata && uusi?.kohta !== tunnus && (
                      <button className="nappi nappi--kevyt" data-ltar-lisaa={tunnus} style={{ fontSize: 12 }}
                        onClick={() => void avaaUusi(tunnus)}>{tr("ltar.merkinta_lisaa")}</button>
                    )}
                    {saaMuokata && uusi?.kohta === tunnus && (
                      <div data-ltar-uusi={tunnus} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", margin: "4px 0" }}>
                        {/* T653: «missä» ja «mikä vika» pakollisia uudessa punchissa; kuvaus toimii lisätietona. */}
                        <div style={{ flexBasis: "100%" }}><Luokkavalitsin kohde="punch" arvo={{ missa: uusi.missa, vika: uusi.vikaluokka, lisatieto: uusi.vika }}
                          naytaLisatieto={false} muuta={(v) => setUusi({ ...uusi, missa: v.missa, vikaluokka: v.vika })} /></div>
                        <input value={uusi.vika} placeholder={tr("pc.mika_vika")} data-kentta="ltar_merkinta_vika" autoFocus
                          style={{ flex: 1, minWidth: 160 }} onChange={(e) => setUusi({ ...uusi, vika: e.target.value })} />
                        <select value={uusi.luokka} data-kentta="ltar_merkinta_luokka" title={tr("pc.luokka_title")}
                          onChange={(e) => setUusi({ ...uusi, luokka: e.target.value as "" | "A" | "B" | "C" })}>
                          <option value="" disabled>{tr("pc.valitse_luokka")}</option>
                          {(["A", "B", "C"] as const).map((l) => <option key={l} value={l}>{l} · {luokanNimi(l, punchinLuokka(l)?.nimi ?? l)}</option>)}
                        </select>
                        <input value={uusi.hitsi} list={`ltar-hitsit-${linjaId}`} placeholder={tr("ltar.merkinta_hitsi")}
                          data-kentta="ltar_merkinta_hitsi" style={{ width: 200 }} onChange={(e) => setUusi({ ...uusi, hitsi: e.target.value })} />
                        <datalist id={`ltar-hitsit-${linjaId}`}>
                          {(hitsit ?? []).map((h) => <option key={h.id} value={h.hitsinumero}>{h.tarra ?? ""}</option>)}
                        </datalist>
                        <button className="nappi nappi--paa" data-ltar-tallenna={tunnus} onClick={() => void lisaaMerkinta()}>{tr("ltar.merkinta_tallenna")}</button>
                        <button className="nappi nappi--kevyt" onClick={() => { setUusi(null); setUusiVirhe(null); }}>{tr("ltar.merkinta_peru")}</button>
                        {uusiVirhe && <span className="huom" role="alert" style={{ flexBasis: "100%" }}>{uusiVirhe}</span>}
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
        <label className="dim" >{tr("ltar.tarkastaja")}<br />
          <input value={tarkastaja} data-kentta="lt_tarkastaja" disabled={!tunnistetietoMuokattava} onChange={(e) => setTarkastaja(e.target.value)} style={{ width: 150 }} /></label>
        <label className="dim" >{tr("ltar.pvm")}<br />
          <input type="date" value={pvm} disabled={!tunnistetietoMuokattava} onChange={(e) => setPvm(e.target.value)} /></label>
        <label className="dim" style={{ flex: 1, minWidth: 140 }}>{tr("ltar.huomiot")}<br />
          <input value={huomiot} disabled={!tunnistetietoMuokattava} onChange={(e) => setHuomiot(e.target.value)} style={{ width: "100%" }} /></label>
      </div>

      {/* 177 (Toni 26.9.2026): vanha hyväksyntä ilman edustajatietoa — ei arvata, ei luovutettu. */}
      {lukittu && lt?.edustajatieto_puuttuu && (
        <p className="dim" data-edustajatieto="puuttuu" style={{ marginTop: 8 }}>{lt.edustajatieto_puuttuu}</p>
      )}

      {muokattava && (
        <fieldset className="luovutus" data-luovutus="lomake" style={{ marginTop: 10 }}>
          <legend>{tr("ltar.luovutus")}</legend>
          <p className="dim" style={{ margin: "0 0 6px" }}>
            {tr("ltar.luovutus_ohje")}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label className="dim">{tr("ltar.edustaja")}<br />
              <input value={edustaja} data-kentta="asiakkaan_edustaja" placeholder={tr("ltar.nimi")}
                onChange={(e) => setEdustaja(e.target.value)} style={{ width: 200 }} /></label>
          </div>
          {/* 251 (T621, Toni 19.10.2026 klo 11.50): edustajan allekirjoitus sormella tai kynällä nimikentän alla. Tarkastaja ei
              allekirjoita: hänet tunnistetaan kirjautumisesta. Paperipolku: allekirjoitettu pöytäkirja liitteenä (laji 250). */}
          {edustajaPakollinen && (
            <div data-allekirjoitus="lomake" style={{ marginTop: 8 }}>
              <div className="dim">{tr("ltar.allekirjoitus")}</div>
              {lt?.allekirjoitus && !piirretty && (
                <p className="dim" data-allekirjoitus-tallennettu="" style={{ margin: "4px 0" }}>
                  <img src={api.liiteOsoite(lt.allekirjoitus.liite_id)} alt={tr("ltar.allekirjoitus")} style={{ maxHeight: 60, verticalAlign: "middle", background: "#fff" }} />
                  {" "}{tr("ltar.allekirjoitettu", { nimi: lt.allekirjoitus.nimi ?? "", aika: naytaPvm(lt.allekirjoitus.hetki) ?? "" })}
                </p>
              )}
              <Allekirjoitusruutu ref={ruutu} muuttui={setPiirretty} />
              {(lt?.paperipoytakirjoja ?? 0) > 0 && (
                <p className="dim" data-allekirjoitus-paperi={lt!.paperipoytakirjoja} style={{ margin: "4px 0 0" }}>
                  {tr("ltar.paperi_liitteena", { n: lt!.paperipoytakirjoja ?? 0 })}</p>
              )}
            </div>
          )}
          {(lt?.luokittelemattomia ?? 0) > 0 && (
            /* Toni 17.10.2026 klo 07.35: ilmoitus on painike, joka avaa linjan avoimet punchit kuvineen saman sivun päälle —
               lomakkeen täytetyt kentät eivät katoa (paneeli ei lataa lomaketta uudelleen). */
            <button type="button" className="ilmoituspainike" data-luokittelemattomia={lt!.luokittelemattomia}
                    onClick={() => setPaneeliAuki(true)} disabled={punchit === null}>
              {tr("ltar.luokittelemattomia", { n: lt!.luokittelemattomia })} <span className="ilmoituspainike__avaa">{tr("ltar.nayta_punchit")} ›</span>
            </button>
          )}
          {paneeliAuki && punchit && <LinjanPunchPaneeli punchit={punchit} sulje={() => setPaneeliAuki(false)} />}
          {(avoimiaA ?? 0) > 0 && (
            <div data-a-punchit={avoimiaA} style={{ marginTop: 6 }}>
              <p className="huom" style={{ margin: 0 }}>
                {tr("ltar.a_punchit", { n: avoimiaA ?? 0 })}
              </p>
              {lt!.saa_poiketa
                ? <label className="dim" style={{ display: "block", marginTop: 4 }}>{tr("ltar.poikkeaman_syy")}<br />
                    <input value={aPeruste} data-kentta="a_poikkeama_peruste" onChange={(e) => setAPeruste(e.target.value)}
                      style={{ width: "100%" }} /></label>
                : <p className="dim" style={{ margin: "4px 0 0" }}>
                    {tr("ltar.poikkeama_oikeus")}</p>}
            </div>
          )}
        </fieldset>
      )}
      {sharepointissa && saaRoolista && (
        <p className="dim" data-linjatarkastus-sharepoint style={{ marginTop: 10 }}>{tr("ltar.sharepointissa")}</p>
      )}
      {muokattava && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="nappi nappi--kevyt" disabled={tallentaa} onClick={() => tallenna(false)}>{tr("ltar.tallenna")}</button>
          <button className="nappi nappi--paa" disabled={tallentaa || !voiHyvaksya}
            title={!voiHyvaksya ? syy : tr("ltar.hyvaksy_title")}
            onClick={() => tallenna(true)}>{tr("ltar.hyvaksy")}</button>
          {!voiHyvaksya && <span className="dim" data-hyvaksynta-este="">{syy}</span>}
        </div>
      )}

      {/* Luovutettu: kuka, kenelle, milloin — ja allekirjoitettu isometri (ei pakollinen; puuttuva on velkaa). */}
      {lukittu && lt?.luovutus && (
        <div data-luovutus="kirjattu" style={{ marginTop: 10 }}>
          <p style={{ margin: 0, fontSize: 13 }}>
            <b>{tr("ltar.hyvaksytty")}</b> {naytaPvm(lt.luovutus.hetki)}
            {lt.luovutus.asiakkaan_edustaja ? <> · {tr("ltar.edustaja_pieni")} {lt.luovutus.asiakkaan_edustaja}</> : null}
            {" "}· {tr("ltar.hyvaksyja")} {lt.luovutus.luovuttaja}
          </p>
          {/* 251: allekirjoitus tai paperipöytäkirja hyväksytyllä rivillä (lukittu); jo hyväksyttyihin ei vaadita takautuvasti. */}
          {lt.allekirjoitus && (
            <p className="dim" data-allekirjoitus="lukittu" style={{ margin: "4px 0 0" }}>
              <img src={api.liiteOsoite(lt.allekirjoitus.liite_id)} alt={tr("ltar.allekirjoitus")} style={{ maxHeight: 60, verticalAlign: "middle", background: "#fff" }} />
              {" "}{tr("ltar.allekirjoitettu", { nimi: lt.allekirjoitus.nimi ?? "", aika: naytaPvm(lt.allekirjoitus.hetki) ?? "" })}
            </p>
          )}
          {!lt.allekirjoitus && (lt.paperipoytakirjoja ?? 0) > 0 && (
            <p className="dim" data-allekirjoitus="paperi" style={{ margin: "4px 0 0" }}>{tr("ltar.paperi_liitteena", { n: lt.paperipoytakirjoja ?? 0 })}</p>
          )}
          {lt.luovutus.a_poikkeama_peruste && (
            <p className="huom" style={{ margin: "4px 0 0" }}>
              {tr("ltar.poikkeama_kirjattu", { n: lt.luovutus.avoimia_a_hetkella ?? 0 })} · {lt.luovutus.a_poikkeama_peruste}
              {lt.luovutus.poikkeama_kirjaaja ? ` · ${lt.luovutus.poikkeama_kirjaaja}` : ""}
            </p>
          )}
          <div style={{ marginTop: 8 }}>
            <Liitteet kohdeTaulu="luovutus" kohdeId={t!.id} saaMuokata={saaMuokata} otsikko={tr("ltar.allekirjoitettu_isometri")} />
            {lt.luovutusliitteita === 0 && (
              <p className="dim" style={{ margin: "4px 0 0" }}>{tr("ltar.isometri_puuttuu")}</p>
            )}
          </div>
        </div>
      )}
      {lukittu && saaMuokata && (
        <button className="nappi nappi--kevyt" style={{ marginTop: 10, marginRight: 8 }} disabled={tallentaa}
          data-tunnistetieto="tallenna" title={tr("ltar.tunnistetieto_title")}
          onClick={() => void tallenna(false)}>{tr("ltar.tunnistetieto")}</button>
      )}
      {lukittu && saaMuokata && (
        <button className="nappi nappi--kevyt" style={{ marginTop: 10 }} disabled={tallentaa}
          onClick={() => api.tallennaLinjatarkastus(linjaId, { tila: "kesken", kohdat }).then(lataa)}>
          {tr("ltar.avaa_uudelleen")}
        </button>
      )}
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {/* Toni 19.10.2026 klo 11.50: tarkastuskuvat-osio — Selailussa isometrin yläpuolella (kuvatErikseen), muualla tässä. */}
      {!kuvatErikseen && (
        <div style={{ marginTop: 12 }}>
          <LinjatarkastusKuvat linjaId={linjaId} saaMuokata={saaMuokata}
            muuttui={() => void api.luovutustila(linjaId).then(setLt).catch(() => {})} />
        </div>
      )}
    </div>
  );
}

/**
 * Tarkastuskuvat (Toni 19.10.2026 klo 11.50): iso «Lisää tarkastuskuva» -painike avaa tabletilla kameran, galleria omana nappinaan,
 * ja liitteen lajiksi voi valita «allekirjoitettu pöytäkirja» (paperipolku, 250). Kuvat kuuluvat linjatarkastukseen; jos linjalla ei
 * vielä ole tarkastusta, ensimmäinen kuva luo keskeneräisen tarkastuksen.
 * 12.10.2026 (Tonin kuva): pöytäkirja ja kuvat EIVÄT ole henkilötietoa — ei arkaluonteinen-oletusta.
 */
export function LinjatarkastusKuvat({ linjaId, saaMuokata, muuttui }: { linjaId: number; saaMuokata: boolean; muuttui?: () => void }) {
  const [id, setId] = useState<number | null | undefined>(undefined);
  const [tuotu, setTuotu] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lahetetaan, setLahetetaan] = useState(false);
  const kameraSyote = useRef<HTMLInputElement>(null);
  const galleriaSyote = useRef<HTMLInputElement>(null);
  const lataa = useCallback(async () => {
    try { const r = await api.linjatarkastus(linjaId); setId(r?.id ?? null); setTuotu(r?.isantajarjestelma === "sharepoint"); }
    catch { setId(null); }
  }, [linjaId]);
  useEffect(() => { void lataa(); }, [lataa]);
  // T623 (Toni 19.10.2026 klo 12.56): yksi painallus — keskeneräinen tarkastus syntyy vasta, kun ensimmäinen kuva on luettu ja
  // tallentuu. Peruttu kamera tai tyhjä valinta ei luo mitään (ennen: ensimmäinen painallus loi tyhjän tarkastuksen).
  async function aloitaKuvalla(tiedostot: FileList | null) {
    if (!tiedostot?.length) return;
    setLahetetaan(true); setVirhe(null);
    try {
      const luetut = await Promise.all(Array.from(tiedostot).map(async (f) => ({ nimi: f.name, sisalto: await lueBase64(f) })));
      await api.tallennaLinjatarkastus(linjaId, { kohdat: {} });
      const uusi = (await api.linjatarkastus(linjaId))?.id;
      if (!uusi) throw new Error(tr("ltar.tallennus_virhe"));
      for (const l of luetut) await api.lisaaLiite({ kohde_taulu: "linjatarkastus", kohde_id: uusi, nimi: l.nimi, sisalto: l.sisalto });
      await lataa(); await muuttui?.();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally {
      setLahetetaan(false);
      if (kameraSyote.current) kameraSyote.current.value = "";
      if (galleriaSyote.current) galleriaSyote.current.value = "";
    }
  }
  if (id === undefined) return null;
  if (id === null) {
    if (!saaMuokata) return null;
    return (
      <div className="liitteet" data-tarkastuskuvat="ei-tarkastusta">
        <h3>{tr("ltar.tarkastuskuvat")}</h3>
        <div className="liitteet__lisays">
          {/* Toni 19.10.2026 klo 19.45: päätoimintonapin tyyli, kosketusalue vähintään 44 px, kamerakuvake tekstin edessä. */}
          <button type="button" className="nappi nappi--paa nappi--iso nappi--kuvake" disabled={lahetetaan}
            onClick={() => kameraSyote.current?.click()} data-tarkastuskuva-kamera="">
            <KameraKuvake />{tr("ltar.lisaa_tarkastuskuva")}</button>
          <input hidden ref={kameraSyote} type="file" accept="image/*" capture="environment" data-tarkastuskuva-syote="kamera"
            onChange={(e) => void aloitaKuvalla(e.target.files)} disabled={lahetetaan} />
          <button type="button" className="nappi nappi--kevyt" disabled={lahetetaan}
            onClick={() => galleriaSyote.current?.click()} data-tarkastuskuva-galleria="">{tr("liite.galleriasta")}</button>
          <input hidden ref={galleriaSyote} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            data-tarkastuskuva-syote="galleria" onChange={(e) => void aloitaKuvalla(e.target.files)} disabled={lahetetaan} />
          {lahetetaan && <span className="dim">{tr("liite.lahetetaan")}</span>}
        </div>
        <p className="ikkuna__vihje">{tr("ltar.tarkastuskuva_vihje")}</p>
        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </div>
    );
  }
  // Toni 19.10.2026 klo 12.15 kohta 2a: tuotuun (SharePoint) tarkastukseen ei lisätä mitään — liitteet näkyvät, lisäystä ei ole
  // (palvelin vastaa samoin 409 linjatarkastus_tuotu).
  return (
    <div data-tarkastuskuvat={id} data-tarkastuskuvat-tuotu={tuotu ? "" : undefined}>
      {tuotu && saaMuokata && <p className="dim" style={{ margin: "0 0 4px" }}>{tr("virhe.linjatarkastus_tuotu")}</p>}
      <Liitteet kohdeTaulu="linjatarkastus" kohdeId={id} saaMuokata={saaMuokata && !tuotu} otsikko={tr("ltar.tarkastuskuvat")}
        pienoiskuvat kamera iso paatoiminto nimettavat lajit={["allekirjoitettu_poytakirja"]}
        lisaaTeksti={tr("ltar.lisaa_tarkastuskuva")} vihje={tr("ltar.tarkastuskuva_vihje")} muuttui={muuttui} />
    </div>
  );
}
